import {
  CalendarInput,
  ConfirmModal,
  CustomCurrencyInput,
  CustomInput,
  CustomSelect,
} from 'app/components';
import AttentionCircularIcon from 'app/components/Icons/AttentionCircularIcon';
import { formatCPF, formatDecimalField, formatPhone } from 'app/helpers';
import { routingPath } from 'app/routes';
import { formatBRLCurrencyMaskInput } from 'app/utils/currency';
import { useFormik } from 'formik';
import {
  EMaritalPropertySystem,
  EMaritalStatus,
  ESex,
  maritalStatusLabel,
  maritalStatusOptions,
  sexOptionLabel,
  TRegisterPersonDataRequest,
} from 'modules/customer/context/PersonProvider/person.interfaces';
import { aboutStepSchema } from 'modules/customer/helpers/personValidation';
import { usePersonService } from 'modules/customer/services';
import { useCallback, useEffect, useState } from 'react';
import {
  educationLevelOptions,
  EEducationLevel,
  EEducationLevelDescription,
  IPersonForm,
  workRelationOptions,
} from '../../types';
import {
  AboutStepExistingPerson,
  StepContentInput,
  StepContentInputArea,
} from './styles';
import { IAddress } from '../../Address/types';
import { isValidCPF } from 'app/utils/validations';

export type TReactChangeInput = React.ChangeEvent<
  HTMLInputElement | HTMLTextAreaElement
>;

interface ICreatePersonFormProps {
  setPerson: (person: IPersonForm) => void;
  person: IPersonForm;
  isRepresentative?: boolean;
  setRepresentativeAddres?: (values?: IAddress) => void;
  updateExistsPerson: (
    existsPerson?: TRegisterPersonDataRequest | null,
  ) => void;
  existsPerson?: TRegisterPersonDataRequest | null;
  hasTaxId?: string;
}

const CreatePersonForm = ({
  setPerson,
  person,
  isRepresentative,
  setRepresentativeAddres,
  existsPerson,
  updateExistsPerson,
  hasTaxId,
}: ICreatePersonFormProps) => {
  const { getPersonList } = usePersonService();

  const [isLoading, setIsLoading] = useState(false);
  const [isExistingPersonModalVisible, toggleExistingPersonModalVisible] =
    useState(false);

  const formik = useFormik({
    initialValues: person || ({} as IPersonForm),
    validationSchema: aboutStepSchema,
    onSubmit: (values) => console.info(values),
  });

  useEffect(() => {
    if (hasTaxId && isValidCPF(hasTaxId)) {
      formik.setFieldValue('taxpayer_id', hasTaxId);
      onChangeTaxpayerId(hasTaxId);
    }
  }, [hasTaxId]);

  const onChangeTaxpayerId = useCallback(
    async (taxpayerId: string): Promise<boolean> => {
      if (!isValidCPF(taxpayerId)) return false;

      try {
        setIsLoading(true);
        const personResponse = await getPersonList(
          0,
          'taxpayer_id',
          taxpayerId,
          undefined,
          true,
        );
        if (personResponse && personResponse.content.length > 0) {
          const findPerson = personResponse.content[0];

          if (hasTaxId) {
            // Preenchimento automático do formulário
            const parsedAddress = {
              city: {
                label: findPerson?.address?.city || '',
                value: findPerson?.address?.city || '',
              },
              country_code: findPerson?.address?.country_code,
              district: findPerson?.address?.district || 'não informado',
              postal_code: findPerson?.address?.postal_code,
              state_code: {
                label: findPerson?.address?.state_code || '',
                value: findPerson?.address?.state_code || '',
              },
              street_name: findPerson?.address?.street_name || 'não informado',
              street_number: findPerson?.address?.street_number || '0',
            };

            const parsedPersonForm: IPersonForm = {
              address: parsedAddress,
              birth_date: findPerson?.birth_date,
              birthplace: findPerson?.birthplace,
              identification: {
                document_issue_date: findPerson?.id_document?.issue_date,
                document_issuer: findPerson?.id_document?.issuer,
                document_number: findPerson?.id_document?.number,
                document_type: findPerson.id_document?.type
                  ? {
                      label: findPerson.id_document?.type,
                      value: findPerson.id_document?.type,
                    }
                  : undefined,
              },
              email_address: findPerson?.email_address,
              full_name: findPerson?.full_name,
              marital_status: {
                label: findPerson?.marital_status
                  ? maritalStatusLabel[findPerson?.marital_status]
                  : 'Não informado',
                value: findPerson?.marital_status || EMaritalStatus.OTHER,
              },
              monthly_income: findPerson?.monthly_income?.toString(),
              mothers_name: findPerson?.mothers_name,
              nationality: findPerson?.nationality,
              occupation: findPerson?.occupation,
              pep: {
                label: findPerson?.pep ? 'Sim' : 'Não',
                value: findPerson?.pep ? 'true' : 'false',
              },
              sex: {
                label: findPerson?.sex
                  ? sexOptionLabel[findPerson?.sex]
                  : 'Não informado',
                value: findPerson?.sex ?? ESex.OTHER,
              },
              phone: `${findPerson?.phone.area_code}${findPerson?.phone.number}`,
              taxpayer_id: findPerson?.taxpayer_id,
              id: findPerson?.id,
              workRelation: undefined,
              signer: undefined,
              aditional_information: undefined,
              share: 0,
            };

            formik.setValues(parsedPersonForm);
            setPerson(parsedPersonForm);

            return true;
          } else {
            const parsedPerson: IPersonForm = {
              id: findPerson?.id,
              full_name: findPerson?.full_name,
            };

            updateExistsPerson(personResponse.content[0]);
            setPerson(parsedPerson);
            toggleExistingPersonModalVisible(true);
            return true;
          }
        }
        updateExistsPerson(null);
        return false;
      } catch {
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [formik, setPerson, hasTaxId],
  );

  const getFormatedPhoneFromExistingPerson = useCallback(() => {
    if (existsPerson) {
      const phoneNumber = existsPerson?.phone?.number || '';
      const areaCodeNumber = existsPerson?.phone?.area_code || '';

      if (!phoneNumber || !areaCodeNumber) return null;
      return formatPhone(areaCodeNumber + phoneNumber);
    }
    return null;
  }, [existsPerson]);

  const handleMonthlyIncomeChange = useCallback((evt: TReactChangeInput) => {
    formik.setFieldValue(
      'monthly_income',
      formatBRLCurrencyMaskInput(evt.target.value || ''),
    );
  }, []);

  const handleShare = (value: string) => {
    return formatDecimalField(value);
  };

  useEffect(() => {
    setPerson(formik.values);
  }, [formik.values]);

  return (
    <>
      <StepContentInputArea>
        <StepContentInput>
          <CustomInput
            name="taxpayer_id"
            disabled={!!hasTaxId}
            id="taxpayer_id"
            isLoading={isLoading}
            value={
              hasTaxId
                ? formatCPF(hasTaxId)
                : formatCPF(formik?.values.taxpayer_id || '')
            }
            handleInputChange={(e) => {
              formik.handleChange(e);
              const taxpayerId = e.currentTarget.value;
              if (taxpayerId.length >= 14) {
                onChangeTaxpayerId(taxpayerId.replace(/\D+/g, ''));
              }
            }}
            onBlur={formik.handleBlur}
            placeholder="000.000.000-00"
            labelValue="CPF*"
            validationError={
              formik.touched.taxpayer_id ? formik.errors.taxpayer_id : ''
            }
          />
        </StepContentInput>
        <StepContentInput>
          <CustomInput
            name="full_name"
            value={formik.values?.full_name || ''}
            handleInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Ex.: João da Silva"
            labelValue="Nome completo*"
            validationError={
              formik.touched.full_name ? formik.errors.full_name : ''
            }
          />
        </StepContentInput>
        <StepContentInput>
          <CustomInput
            name="nationality"
            value={formik.values?.nationality || ''}
            handleInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Ex.: Brasileiro"
            labelValue="Nacionalidade*"
            validationError={
              formik.touched.nationality ? formik.errors.nationality : ''
            }
          />
        </StepContentInput>
        <StepContentInput>
          <CustomInput
            name="occupation"
            value={formik.values?.occupation || ''}
            handleInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="Ex.: Vendedor"
            labelValue="Ocupação*"
            validationError={
              formik.touched.occupation ? formik.errors.occupation : ''
            }
          />
        </StepContentInput>
        <StepContentInput>
          <CustomInput
            name="phone"
            value={formatPhone(formik.values?.phone || '')}
            handleInputChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="(00) 00000-0000"
            labelValue="Telefone*"
            validationError={formik.touched.phone ? formik.errors.phone : ''}
          />
        </StepContentInput>
        <StepContentInput>
          <CustomInput
            name="email_address"
            value={formik.values?.email_address || ''}
            handleInputChange={formik.handleChange}
            placeholder="exemplo@email.com"
            labelValue="E-mail*"
            validationError={
              formik.touched.email_address ? formik.errors.email_address : ''
            }
          />
        </StepContentInput>
        <StepContentInput>
          <CalendarInput
            name="birth_date"
            labelValue="Data de nascimento*"
            placeholder="dd/mm/aaaa"
            value={formik.values?.birth_date || ''}
            onChange={(e) =>
              formik.handleChange({
                currentTarget: {
                  value: e,
                  name: 'birth_date',
                },
              })
            }
            validationError={
              formik.touched.birth_date ? formik.errors.birth_date : ''
            }
          />
        </StepContentInput>
        <StepContentInput>
          <CustomInput
            name="birthplace"
            value={formik.values?.birthplace || ''}
            handleInputChange={formik.handleChange}
            placeholder="Ex.: São Paulo"
            labelValue="Naturalidade"
            validationError={
              formik.touched.birthplace ? formik.errors.birthplace : ''
            }
          />
        </StepContentInput>
        <StepContentInput>
          <CustomInput
            name="mothers_name"
            value={formik.values?.mothers_name || ''}
            handleInputChange={formik.handleChange}
            placeholder="Ex.: Maria da Silva"
            labelValue="Nome da mãe*"
            validationError={
              formik.touched.mothers_name ? formik.errors.mothers_name : ''
            }
          />
        </StepContentInput>
        <StepContentInput>
          <CustomSelect
            labelValue="Sexo"
            name="sex"
            placeholder="Selecione"
            value={formik.values.sex}
            handleInputChange={(v) => {
              formik.handleChange(v);
            }}
            options={[
              { value: ESex.MALE, label: 'Masculino' },
              { value: ESex.FEMALE, label: 'Feminino' },
              { value: ESex.OTHER, label: 'Não informado' },
            ]}
            validationError={formik.touched.sex ? formik.errors.sex : ''}
          />
        </StepContentInput>
        <StepContentInput>
          <CustomSelect
            labelValue="Escolaridade"
            name="education_level"
            isClearable={false}
            value={
              formik.values.education_level
                ? {
                    label:
                      EEducationLevelDescription[formik.values.education_level],
                    value: formik.values.education_level,
                  }
                : {
                    label:
                      EEducationLevelDescription[EEducationLevel.NOT_INFORMED],
                    value: EEducationLevel.NOT_INFORMED,
                  }
            }
            handleInputChange={(v) => {
              formik.setValues({
                ...formik.values,
                education_level: v.target.value.value,
              });
            }}
            options={educationLevelOptions}
          />
        </StepContentInput>
        <StepContentInput>
          <CustomSelect
            labelValue="Estado civil"
            name="marital_status"
            placeholder="Selecione"
            value={formik.values.marital_status}
            handleInputChange={(v) => {
              formik.handleChange(v);
            }}
            options={maritalStatusOptions}
            validationError={
              formik.touched.marital_status ? formik.errors.marital_status : ''
            }
          />
        </StepContentInput>
        {formik.values.marital_status?.value === EMaritalStatus.MARRIED && (
          <>
            <StepContentInput>
              <CustomInput
                name="spouse_full_name"
                value={formik.values.spouse_full_name || ''}
                handleInputChange={formik.handleChange}
                placeholder="Ex.: Maria da Silva"
                labelValue="Nome cônjuge"
                validationError={
                  formik.touched.spouse_full_name
                    ? formik.errors.spouse_full_name
                    : ''
                }
              />
            </StepContentInput>
            <StepContentInput>
              <CustomInput
                name="spouse_taxpayer_id"
                value={formatCPF(formik.values.spouse_taxpayer_id || '')}
                handleInputChange={formik.handleChange}
                placeholder="000.000.000-00"
                labelValue="CPF cônjuge"
                validationError={
                  formik.touched.spouse_taxpayer_id
                    ? formik.errors.spouse_taxpayer_id
                    : ''
                }
              />
            </StepContentInput>
            <StepContentInput>
              <CalendarInput
                name="spouse_date_of_birth"
                labelValue="Data de nascimento cônjuge"
                placeholder="dd/mm/aaaa"
                value={formik.values.spouse_date_of_birth || ''}
                onChange={(e) =>
                  formik.handleChange({
                    currentTarget: {
                      value: e,
                      name: 'spouse_date_of_birth',
                    },
                  })
                }
                validationError={
                  formik.touched.spouse_date_of_birth
                    ? formik.errors.spouse_date_of_birth
                    : ''
                }
              />
            </StepContentInput>
            <StepContentInput>
              <CustomSelect
                labelValue="Regime de bens"
                name="marital_property_system"
                placeholder="Selecione"
                value={formik.values.marital_property_system}
                handleInputChange={(v) => {
                  formik.handleChange(v);
                }}
                options={[
                  {
                    value: EMaritalPropertySystem.FINAL_COMMUNION,
                    label: 'Participação final nos aquestros',
                  },
                  {
                    value: EMaritalPropertySystem.FULL_COMMUNION,
                    label: 'Comunhão universal de bens',
                  },
                  {
                    value: EMaritalPropertySystem.NO_COMMUNION,
                    label: 'Separação total de bens',
                  },
                  {
                    value: EMaritalPropertySystem.PARTIAL_COMMUNION,
                    label: 'Comunhão parcial de bens',
                  },
                ]}
                validationError={
                  formik.touched.marital_property_system
                    ? formik.errors.marital_property_system
                    : ''
                }
              />
            </StepContentInput>
          </>
        )}
        <StepContentInput>
          <CustomCurrencyInput
            labelValue="Renda mensal"
            value={formik.values?.monthly_income || ''}
            handleInputChange={handleMonthlyIncomeChange}
            name="monthly_income"
            validationError={
              formik.touched.monthly_income ? formik.errors.monthly_income : ''
            }
          />
        </StepContentInput>
        <StepContentInput>
          <CustomSelect
            labelValue="Pessoa politicamente exposta (PEP)"
            name="pep"
            placeholder="Selecione"
            value={formik.values.pep}
            handleInputChange={formik.handleChange}
            options={[
              { value: 'true', label: 'Sim' },
              { value: 'false', label: 'Não' },
            ]}
          />
        </StepContentInput>

        {isRepresentative && (
          <>
            <StepContentInput>
              <CustomSelect
                labelValue="Assina pela empresa? *"
                name="signer"
                placeholder="Selecione"
                value={formik.values.signer}
                handleInputChange={formik.handleChange}
                options={[
                  { value: 'true', label: 'Sim' },
                  { value: 'false', label: 'Não' },
                ]}
              />
            </StepContentInput>
            <StepContentInput>
              <CustomSelect
                labelValue="Vínculo *"
                name="workRelation"
                placeholder="Selecione"
                value={formik.values.workRelation}
                handleInputChange={formik.handleChange}
                options={workRelationOptions}
              />
            </StepContentInput>
            <StepContentInput>
              <CustomInput
                labelValue="Participação em quotas (%)"
                placeholder="Ex: 50"
                value={formik.values?.share?.toString() || ''}
                handleInputChange={(e) => {
                  formik.setFieldValue('share', handleShare(e.target.value));
                }}
                validationError={
                  formik.touched.share ? formik.errors.share : ''
                }
              />
            </StepContentInput>
          </>
        )}
      </StepContentInputArea>

      <ConfirmModal
        isColumnButtons={true}
        isOpen={isExistingPersonModalVisible}
        icon={<AttentionCircularIcon />}
        title="O CPF informado já está cadastrado"
        btnConfirmText={
          isRepresentative
            ? 'Adicionar como representante'
            : 'Limpar e informar outro CPF'
        }
        handleConfirm={() => {
          if (isRepresentative) {
            const parsedAddress = {
              city: {
                label: existsPerson?.address?.city || '',
                value: existsPerson?.address?.city || '',
              },
              country_code: existsPerson?.address?.country_code,
              district: existsPerson?.address?.district || 'não informado',
              postal_code: existsPerson?.address?.postal_code,
              state_code: {
                label: existsPerson?.address?.state_code || '',
                value: existsPerson?.address?.state_code || '',
              },
              street_name:
                existsPerson?.address?.street_name || 'não informado',
              street_number: existsPerson?.address?.street_number || '0',
            };

            const parsedPersonForm: IPersonForm = {
              address: parsedAddress,
              birth_date: existsPerson?.birth_date,
              birthplace: existsPerson?.birthplace,
              identification: {
                document_issue_date: existsPerson?.id_document?.issue_date,
                document_issuer: existsPerson?.id_document?.issuer,
                document_number: existsPerson?.id_document?.number,
              },
              email_address: existsPerson?.email_address,
              full_name: existsPerson?.full_name,
              marital_status: {
                label: existsPerson?.marital_status || '',
                value: existsPerson?.marital_status || '',
              },
              monthly_income: existsPerson?.monthly_income?.toString(),
              mothers_name: existsPerson?.mothers_name,
              nationality: existsPerson?.nationality,
              occupation: existsPerson?.occupation,
              pep: {
                label: existsPerson?.pep ? 'Sim' : 'Não',
                value: existsPerson?.pep ? 'Sim' : 'Não',
              },
              sex: {
                label:
                  existsPerson?.sex === 'MALE'
                    ? 'Masculino'
                    : existsPerson?.sex === 'FEMALE'
                    ? 'Feminino'
                    : 'Não informado',
                value:
                  existsPerson?.sex === 'MALE'
                    ? 'Masculino'
                    : existsPerson?.sex === 'FEMALE'
                    ? 'Feminino'
                    : 'Não informado',
              },
              phone: `${existsPerson?.phone.area_code}${existsPerson?.phone.number}`,
              taxpayer_id: existsPerson?.taxpayer_id,
              id: existsPerson?.id,
              workRelation: undefined,
              signer: undefined,
              aditional_information: undefined,
              share: 0,
            };

            formik.setValues(parsedPersonForm);

            if (typeof setRepresentativeAddres === 'function') {
              setRepresentativeAddres(parsedAddress);
            }

            toggleExistingPersonModalVisible(false);
            return;
          }

          formik.setFieldValue('taxpayer_id', '');
          formik.setFieldTouched('taxpayer_id', false);
          updateExistsPerson(null);
          toggleExistingPersonModalVisible(false);
          setTimeout(() => {
            document.querySelector<HTMLInputElement>('#taxpayer_id')?.focus();
          });
        }}
        handleClose={() => {
          toggleExistingPersonModalVisible(false);
          updateExistsPerson(null);
        }}
        handleCancel={() => {
          toggleExistingPersonModalVisible(false);
          updateExistsPerson(null);
        }}
      >
        <AboutStepExistingPerson>
          <span style={{ marginBottom: '8px' }}>
            <strong>{formatCPF(existsPerson?.taxpayer_id || '')}</strong>
          </span>
          <span>{existsPerson?.full_name}</span>
          {getFormatedPhoneFromExistingPerson() && (
            <span>{getFormatedPhoneFromExistingPerson()}</span>
          )}
          <span>{existsPerson?.email_address}</span>
          <span>...</span>
          {!isRepresentative && (
            <a
              href={`#/${routingPath.records.natural}/${existsPerson?.id}`}
              target="_blank"
              className="link"
              rel="noreferrer"
            >
              Abrir perfil completo em uma nova aba
            </a>
          )}
        </AboutStepExistingPerson>
      </ConfirmModal>
    </>
  );
};

export default CreatePersonForm;
